<template>
    <div class="mt-5">
        <h3 class="mb-4 text-lg font-medium leading-6 dark:text-white">
            Conferência de ebook
        </h3>

        <card>
            <div
                class="relative flex w-full items-center justify-center rounded-lg border-2 border-dashed"
                :class="
                    arrastando
                        ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-300 dark:border-gray-600'
                "
                @dragover.prevent="arrastando = true"
                @dragleave.prevent="arrastando = false"
                @drop.prevent="soltar"
            >
                <label
                    for="arquivo_conferencia"
                    class="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg bg-gray-50 py-6 hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                >
                    <CloudArrowUpIcon class="mb-3 h-10 w-10 text-gray-400" />

                    <p
                        v-if="!arquivo"
                        class="mb-1 text-sm text-gray-500 dark:text-gray-400"
                    >
                        <span class="font-semibold">Clique para selecionar</span>
                        ou arraste e solte
                    </p>
                    <p v-else class="mb-1 text-sm font-medium dark:text-white">
                        {{ arquivo.name }}
                        <span class="text-gray-500 dark:text-gray-400">
                            ({{ emMega(arquivo.size) }})
                        </span>
                    </p>

                    <p class="text-xs text-gray-500 dark:text-gray-400">
                        PDF ou EPUB
                    </p>

                    <input
                        id="arquivo_conferencia"
                        ref="entrada"
                        type="file"
                        accept=".pdf,.epub"
                        class="hidden"
                        @change="selecionar($event.target.files[0])"
                    />
                </label>
            </div>

            <div class="mt-4 flex items-center gap-3">
                <button
                    type="button"
                    class="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    :disabled="!arquivo || carregando"
                    @click="conferir"
                >
                    {{ carregando ? "Conferindo..." : "Conferir arquivo" }}
                </button>

                <p v-if="erro" class="text-sm text-red-500">{{ erro }}</p>
            </div>
        </card>

        <card v-if="resultado" class="mt-5">
            <div v-if="!resultado.encontrado">
                <p class="font-bold text-amber-500">
                    Nenhuma marca encontrada neste arquivo
                </p>
                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Ou o arquivo não saiu da Ticto, ou passou por uma ferramenta
                    que removeu as camadas de rastreio.
                </p>
            </div>

            <div v-else>
                <div
                    class="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-200 pb-3 dark:border-gray-700"
                >
                    <p class="font-bold text-emerald-500">Cópia identificada</p>
                    <p class="text-xs uppercase text-gray-500 dark:text-gray-400">
                        marca lida em {{ rotuloOrigem }}
                    </p>
                </div>

                <dl class="mt-4 grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
                    <div v-for="campo in campos" :key="campo.rotulo">
                        <dt class="text-gray-500 dark:text-gray-400">
                            {{ campo.rotulo }}
                        </dt>
                        <dd class="dark:text-white">{{ campo.valor }}</dd>
                    </div>
                </dl>

                <p v-if="!resultado.pedido" class="mt-4 text-sm text-amber-500">
                    A marca aponta para o pedido
                    {{ resultado.referencia }}, que não está mais na base.
                </p>

                <div v-if="resultado.downloads.length" class="mt-6">
                    <p class="mb-2 text-sm font-medium dark:text-white">
                        Downloads deste comprador
                        <span class="text-gray-500 dark:text-gray-400">
                            ({{ resultado.downloads.length }})
                        </span>
                    </p>

                    <div class="overflow-x-auto">
                        <table class="w-full text-left text-sm">
                            <thead
                                class="text-xs uppercase text-gray-500 dark:text-gray-400"
                            >
                                <tr>
                                    <th class="py-2 pr-4">Data</th>
                                    <th class="py-2 pr-4">Ebook</th>
                                    <th class="py-2 pr-4">Versão</th>
                                    <th class="py-2">IP</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(item, indice) in resultado.downloads"
                                    :key="indice"
                                    class="border-t border-gray-100 dark:border-gray-800"
                                    :class="
                                        indice % 2
                                            ? ''
                                            : 'bg-gray-50 dark:bg-gray-900/40'
                                    "
                                >
                                    <td class="py-2 pr-4 dark:text-white">
                                        {{ emDataHora(item.created_at) }}
                                    </td>
                                    <td class="py-2 pr-4 dark:text-white">
                                        {{ item.ebook || "-" }}
                                    </td>
                                    <td class="py-2 pr-4 dark:text-white">
                                        {{ rotuloQualidade(item.quality) }}
                                    </td>
                                    <td class="py-2 text-gray-500 dark:text-gray-400">
                                        {{ item.ip_address }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <p
                    v-else-if="resultado.pedido"
                    class="mt-6 text-sm text-gray-500 dark:text-gray-400"
                >
                    Nenhum download registrado para este comprador.
                </p>
            </div>
        </card>
    </div>
</template>

<script>
import { CloudArrowUpIcon } from "@heroicons/vue/24/outline";

const ORIGENS = {
    conteudo: "conteúdo da página",
    metadados: "metadados do arquivo",
    epub: "metadados do epub",
    fontes: "metadados do arquivo",
    faixa: "metadados do arquivo",
};

const QUALIDADES = {
    original: "Original",
    compressed: "Compactado",
};

export default {
    name: "ConferenciaEbook",
    components: { CloudArrowUpIcon },
    data() {
        return {
            arquivo: null,
            arrastando: false,
            carregando: false,
            erro: null,
            resultado: null,
        };
    },
    computed: {
        rotuloOrigem() {
            return ORIGENS[this.resultado?.origem] ?? this.resultado?.origem;
        },
        campos() {
            const pedido = this.resultado?.pedido;
            const comprador = pedido?.comprador ?? {};

            return [
                { rotulo: "Pedido", valor: this.resultado.referencia },
                { rotulo: "Comprador", valor: comprador.nome || "-" },
                { rotulo: "E-mail", valor: comprador.email || "-" },
                {
                    rotulo: "Documento",
                    valor: this.emDocumento(comprador.documento),
                },
                {
                    rotulo: "Data do pedido",
                    valor: this.emDataHora(pedido?.criado_em),
                },
            ];
        },
    },
    methods: {
        selecionar(arquivo) {
            this.arquivo = arquivo ?? null;
            this.resultado = null;
            this.erro = null;
        },
        soltar(evento) {
            this.arrastando = false;
            this.selecionar(evento.dataTransfer.files[0]);
        },
        emMega(bytes) {
            if (!bytes) return "";

            const mega = bytes / 1024 / 1024;

            return mega < 1
                ? `${(bytes / 1024).toFixed(0)} KB`
                : `${mega.toFixed(1)} MB`;
        },
        emDataHora(valor) {
            if (!valor) return "-";

            const data = new Date(valor);

            return Number.isNaN(data.getTime())
                ? valor
                : data.toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                  });
        },
        emDocumento(valor) {
            const digitos = String(valor ?? "").replace(/\D/g, "");

            if (digitos.length === 11) {
                return digitos.replace(
                    /(\d{3})(\d{3})(\d{3})(\d{2})/,
                    "$1.$2.$3-$4"
                );
            }

            if (digitos.length === 14) {
                return digitos.replace(
                    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                    "$1.$2.$3/$4-$5"
                );
            }

            return valor || "-";
        },
        rotuloQualidade(valor) {
            return QUALIDADES[valor] ?? valor ?? "-";
        },
        async conferir() {
            this.carregando = true;
            this.erro = null;
            this.resultado = null;

            try {
                const caminho = await this.enviar(this.arquivo);
                const { data } = await axios.post(
                    "/api/ebooks/verificar-marca",
                    { caminho }
                );

                this.resultado = data;
            } catch (erro) {
                this.erro =
                    erro?.response?.data?.message ??
                    "Não foi possível conferir o arquivo.";
            } finally {
                this.carregando = false;
            }
        },
        async enviar(arquivo) {
            const extensao = arquivo.name.split(".").pop().toLowerCase();
            const { data } = await axios.post("/api/ebooks/url-de-upload", {
                extensao,
            });

            const headers = { ...data.headers };
            delete headers.Host;

            await axios.put(data.url, arquivo, {
                headers,
                withCredentials: false,
            });

            return data.caminho;
        },
    },
};
</script>
